import { useEffect, useRef, useState } from 'react';
import { FilterOperatorEnum, SearchTabEnum } from '@vivli/features/search/infrastructure/enum';
import { ISearchStudy } from '@vivli/features/dashboard/infrastructure/interface';
import {
    IAutocompleteItem,
    IAzureFiltersSearchApi,
    IFilterSet,
    ISampleSize,
    ISelectOption
} from '@vivli/features/search/infrastructure/interface';
import { sanitizeCsvValue, useCsvExportHook, useQueryParamsHook } from '@vivli/shared/infrastructure/hook';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useFacetsHook } from '@vivli/features/search/infrastructure/hook';
import { finalize, first, map, mergeMap, toArray } from 'rxjs/operators';
import { IConfig, IDateRange, IToastApi } from '@vivli/shared/infrastructure/interface';
import { from } from 'rxjs';
import ReactGA from 'react-ga4';
import { LoggerService } from '@vivli/shared/infrastructure/service';
import { IActiveUser, IAssignedAppType, ISearchApi, ISearchIndexApi } from '@vivli/core/infrastructure/interface';

const CSV_HEADERS = [
    'NCTID',
    'Sponsor Protocol Id',
    'Title',
    'Acronym',
    'Condition or Disease',
    'Intervention/Treatment',
    'Therapeutic Area',
    'Phase',
    'Number Enrolled',
    'Contributor',
    'Lead Sponsor Agency',
    'Funder',
    'Data Accessibility',
    'Data Availability',
    'Primary Registry URL',
    'URL to Request Study from Sponsor',
    'Other Resources for Study',
    'Primary DOI',
    'Brief Summary',
    'Additional Information',
    'Ages Eligible For Study',
    'Sexes Eligible For Study',
    'Accepts Healthy Volunteers',
    'Locations',
    'Public Disclosures',
    'VivliURL',
    'Study Posted Date'
];

const PAGE_LENGTH = 15;

/**
 * How many export pages may be in flight at once.
 *
 * DEV-1133: the CSV export used to fan out with no limit, which was Azure's load to absorb. Those
 * requests hit our own API now, so the burst is bounded. Note this caps concurrency, not the total
 * request count - the rate limit cares about the total, and is sized for it separately.
 */
const EXPORT_PAGE_CONCURRENCY = 4;
const DEFAULT_EARLIEST_YEAR = 2004;

interface ISearchApiProps {
    appConfig: IConfig;
    toastApi: IToastApi;
    assignedAppType: IAssignedAppType;
    activeUser: IActiveUser;
    studiesSearch: ISearchIndexApi;
    filtersSearch: IAzureFiltersSearchApi;
    vocabSearch: ISearchIndexApi;
}

export const useSearchApi = ({
    appConfig,
    toastApi,
    assignedAppType,
    activeUser,
    studiesSearch,
    filtersSearch,
    vocabSearch
}: ISearchApiProps): ISearchApi => {
    const { isAmr } = assignedAppType;

    const [retrievingStudiesCsv, setRetrievingStudiesCsv] = useState(false);
    const [activeTab, setActiveTab] = useState<SearchTabEnum>(SearchTabEnum.FirstTab);
    const [isShowingResults, setIsShowingResults] = useState(false);
    const [searchResults, setSearchResults] = useState<ISearchStudy[]>([]);
    const [searchResultCount, setSearchResultCount] = useState<number>();
    const [isGettingStudies, setIsGettingStudies] = useState(false);
    const [autoCompleteItems, setAutoCompleteItems] = useState<IAutocompleteItem[]>([]);
    const [searchInput, setSearchInput] = useState(appConfig?.initialSearch || '');
    const [filterSets, setFilterSets] = useState<IFilterSet[]>([]);
    const [filterString, setFilterString] = useState<string>('');

    const { search } = useQueryParamsHook();
    const { saveStringToFile } = useCsvExportHook();
    const { ctFilterValues, amrFilterValues, updateCtFilterValues } = useFacetsHook(filtersSearch, isAmr);
    const { hasPermission } = activeUser;

    const currentPageRef = useRef<number>(1);
    const selectStringsRef = useRef<string[]>([]);

    const quoteString = (stringToQuote: string) => {
        // DEV-998: neutralize a formula lead inside the quotes, so the guard survives the wrap.
        return `"${sanitizeCsvValue(stringToQuote)}"`;
    };

    const createStringOutput = (data: ISearchStudy): string => {
        let vivliUrl = `${window.location.origin}/studyDetails/fromSearch/${data.id}`;
        if (hasPermission(PermissionsEnum.DrActAdv)) {
            vivliUrl = `${window.location.origin}/admin/studies/${data.id}/admin-study-feature`;
        }

        const alternativeMetadataDocumentUri = sanitizeCsvValue(data.alternativeMetadataDocumentUri) || '""';
        const externalStudyUri = sanitizeCsvValue(data.externalStudyUri) || '""';
        const nctId = sanitizeCsvValue(data.nctId) || '""';

        const sponsorProtocolId = quoteString(data.sponsorProtocolId);
        const fundersList = data.funder?.length > 0 ? `"${sanitizeCsvValue(data.funder.join(','))}"` : '""';

        const conditions = data.extractedConditions?.length > 0 ? `"${sanitizeCsvValue(data.extractedConditions.join(','))}"` : '""';
        const locations = data.locationsOfStudySites?.length > 0 ? `"${sanitizeCsvValue(data.locationsOfStudySites.join(','))}"` : '""';
        const interventionNames =
            data.extractedInterventions?.length > 0 ? `"${sanitizeCsvValue(data.extractedInterventions.join(','))}"` : '""';
        const therapeuticAreas = data.therapeuticAreas?.length > 0 ? `"${sanitizeCsvValue(data.therapeuticAreas.join(','))}"` : '""';

        const rowData = [
            nctId,
            sponsorProtocolId,
            quoteString(data.title),
            quoteString(data.acronym),
            conditions,
            interventionNames,
            therapeuticAreas,
            sanitizeCsvValue(data.phase),
            sanitizeCsvValue(data.actualEnrollment),
            quoteString(data.orgName),
            quoteString(data.sponsorName),
            fundersList,
            quoteString(data.isDownloadable),
            quoteString(data.isIpdSubmitted),
            sanitizeCsvValue(data.primaryUrl),
            alternativeMetadataDocumentUri,
            externalStudyUri,
            quoteString(data.studyDoi),
            sanitizeCsvValue(data.extractedBriefSummary),
            sanitizeCsvValue(data.additionalInformation),
            sanitizeCsvValue(data.agesEligible),
            quoteString(data.genderEligible),
            sanitizeCsvValue(data.healthyVolunteers),
            locations,
            sanitizeCsvValue(data.publicDisclosuresString),
            vivliUrl,
            data.postedDateString
        ];

        return rowData.join(',');
    };

    const getStudyData = (skip: number = null, select = null, top = PAGE_LENGTH) => {
        return studiesSearch
            .search<ISearchStudy, any>({
                filterSets: filterSets,
                top,
                skip,
                searchText: filterString?.length > 0 ? filterString : '*',
                assignedAppType: assignedAppType.type,
                facets: isAmr ? filtersSearch.amrFacets : filtersSearch.ctFacets,
                select
            })
            .pipe(first());
    };

    const updateStudyData = (skip: number = null) => {
        setIsGettingStudies(true);
        getStudyData(skip).subscribe({
            next: ({ results, count, facets }) => {
                const updatedStudies = skip ? [...searchResults, ...results] : [...results];

                if (!skip) {
                    currentPageRef.current = 0;
                }

                setSearchResults(updatedStudies);
                setSearchResultCount(count);
                setIsGettingStudies(false);

                if (!isAmr && activeTab !== SearchTabEnum.FirstTab) {
                    updateCtFilterValues(facets, true);
                }
            },
            error: (err) => {
                setIsGettingStudies(false);
            },
            complete: () => {
                setIsGettingStudies(false);
            }
        });
    };

    const updateAutoCompleteItems = (autoCompleteString: string) => {
        if (autoCompleteString.length <= 0) {
            setAutoCompleteItems([]);
            return;
        }

        vocabSearch
            .search<IAutocompleteItem>({
                select: selectStringsRef.current,
                top: PAGE_LENGTH,
                searchText: autoCompleteString,
                orderBy: 'label',
                //replaces the raw `isDuplicate eq false` OData string this used to send. It was
                //the only free-form filter on the vocab path and it was constant here, so it is a
                //flag now rather than something the client composes.
                excludeDuplicates: true
            })
            .pipe(first())
            .subscribe(({ results }) => {
                setAutoCompleteItems(results);
            });
    };

    const getNextStudyData = () => {
        currentPageRef.current += 1;
        const skip = currentPageRef.current * PAGE_LENGTH;
        if (skip <= searchResultCount) {
            updateStudyData(skip);
        }
    };

    const callExport = () => {
        setRetrievingStudiesCsv(true);
        const max = 1000;
        const pages = Math.ceil(searchResultCount / max);

        from([...Array(pages).keys()])
            .pipe(
                mergeMap(
                    (page: number) =>
                        getStudyData(page * max, null, max).pipe(
                            mergeMap(({ results }) => results),
                            map((data) => createStringOutput(data))
                        ),
                    //DEV-1133: bounded. Unbounded, a 30,000-row export fired ~30 simultaneous
                    //requests; that was Azure's problem before the proxy and is ours now. This caps
                    //the burst, not the total - the per-minute rate limit is sized for the total
                    //separately, see SearchRateLimiting.DefaultPermitsPerMinute.
                    EXPORT_PAGE_CONCURRENCY
                ),
                toArray(),
                finalize(() => setRetrievingStudiesCsv(false))
            )
            .subscribe({
                error: (err) => toastApi.error('Unable to download studies, please try again.'),
                next: (exportData) => {
                    const formattedDate = new Date().toLocaleDateString();
                    let allExportData = exportData.join('\n');
                    const fileName = `StudySearchResults_${formattedDate}.csv`;
                    allExportData = `${CSV_HEADERS.join(',')}\n${allExportData}`;
                    saveStringToFile(allExportData, fileName);
                }
            });
    };

    const toggleIsShowingResults = () => {
        if (!isShowingResults) {
            LoggerService.debug(`toggleIsShowingResults, search: ${filterString}`);
            ReactGA.send({ hitType: 'pageview', page: `/?searchresults=${filterString}` });
        }
        setIsShowingResults(!isShowingResults);
    };

    const handleFilterSetsUpdate = (sets: IFilterSet[]) => {
        setFilterSets(sets);
        // Update will trigger via useEffect
    };

    const updateFilterString = (updatedFilterString = '') => {
        if (filterString === updatedFilterString) {
            return;
        }

        if (!updatedFilterString) {
            updatedFilterString = '';
        }

        if (location.pathname !== '/' && !location.pathname.includes('/portal/')) {
            return;
        }

        setFilterString(updatedFilterString);
    };

    const updateSelectStrings = (selectStrings: string[]) => {
        selectStringsRef.current = selectStrings;
    };

    const handleBooleanFilter = (key: string, selected: boolean) => {
        if (selected) {
            const newFilterSet: IFilterSet = {
                key,
                itemOperator: FilterOperatorEnum.Or,
                items: [{ value: true }]
            };
            const cleanFilterSets = filterSets.filter((x) => x.key !== key);
            handleFilterSetsUpdate([...cleanFilterSets, newFilterSet]);
        } else if (filterSets.some((x) => x.key === key)) {
            const cleanFilterSets = filterSets.filter((x) => x.key !== key);
            handleFilterSetsUpdate([...cleanFilterSets]);
        }
    };

    const handleDropdownFilter = (key: string, options: ISelectOption[] = [], useAnySyntax = false) => {
        if (options?.length > 0) {
            const newFilterSet: IFilterSet = {
                key,
                operator: useAnySyntax ? FilterOperatorEnum.Any : null,
                itemOperator: FilterOperatorEnum.Or,
                items: options.map((x) => ({ key, value: x.value }))
            };
            const cleanFilterSets = filterSets.filter((x) => x.key !== key);
            handleFilterSetsUpdate([...cleanFilterSets, newFilterSet]);
        } else if (filterSets.some((x) => x.key === key)) {
            const cleanFilterSets = filterSets.filter((x) => x.key !== key);
            handleFilterSetsUpdate([...cleanFilterSets]);
        }
    };

    const handleDateRangeFilter = (key: string, dateRange: IDateRange, isYearRange = false) => {
        if (dateRange) {
            const newFilterSet: IFilterSet = {
                key,
                isYearRange,
                itemOperator: FilterOperatorEnum.And,
                items: [{ value: dateRange }]
            };
            const cleanFilterSets = filterSets.filter((x) => x.key !== key);
            handleFilterSetsUpdate([...cleanFilterSets, newFilterSet]);
        } else if (filterSets.some((x) => x.key === key)) {
            const cleanFilterSets = filterSets.filter((x) => x.key !== key);
            handleFilterSetsUpdate([...cleanFilterSets]);
        }
    };

    const handleSampleSizeFilter = (key: string, sampleSize: ISampleSize) => {
        if (sampleSize) {
            const newFilterSet: IFilterSet = {
                key,
                itemOperator: FilterOperatorEnum.And,
                items: [{ value: sampleSize }]
            };
            const cleanFilterSets = filterSets.filter((x) => x.key !== key);
            handleFilterSetsUpdate([...cleanFilterSets, newFilterSet]);
        } else if (filterSets.some((x) => x.key === key)) {
            const cleanFilterSets = filterSets.filter((x) => x.key !== key);
            handleFilterSetsUpdate([...cleanFilterSets]);
        }
    };

    const handleYearRangeFilter = (key: string, from?: number, to?: number, firstYear?: number) => {
        const fromDate = new Date();
        if (from) {
            fromDate.setFullYear(from);
        } else {
            const initialYear = firstYear ? firstYear : DEFAULT_EARLIEST_YEAR;
            fromDate.setFullYear(initialYear);
        }

        const toDate = new Date();
        if (to) {
            toDate.setFullYear(to);
        } else {
            const thisYear = new Date().getFullYear();
            toDate.setFullYear(thisYear);
        }

        const dateRange: IDateRange = {
            from: fromDate,
            to: toDate
        };

        handleDateRangeFilter(key, dateRange, true);
    };

    const addFilterSet = (filterSet: IFilterSet) => {
        const cleanFilterSets = filterSets.filter((x) => x.key !== filterSet.key);
        handleFilterSetsUpdate([...cleanFilterSets, filterSet]);
    };

    const updateFilterSets = (updatedFilterSets: IFilterSet[]) => {
        const addedSets = updatedFilterSets.filter((ufs) => !filterSets.some((fs) => fs.key === ufs.key));

        const newFilterSets = filterSets.map((fs) => {
            const updatedSet = updatedFilterSets.find((ufs) => ufs.key === fs.key);
            if (updatedSet) {
                return updatedSet;
            }
            return fs;
        });

        handleFilterSetsUpdate([...newFilterSets, ...addedSets]);
    };

    const removeFilterSet = (key: string) => {
        const cleanFilterSets = filterSets.filter((x) => x.key !== key);
        handleFilterSetsUpdate([...cleanFilterSets]);
    };

    const resetFilterSets = () => {
        handleFilterSetsUpdate([]);
    };

    // Effect to update study data when filterSets change
    useEffect(() => {
        updateStudyData();
    }, [filterSets, filterString]);

    // Effect to handle initial search
    useEffect(() => {
        if (search?.length > 0) {
            toggleIsShowingResults();
        }
    }, []);

    return {
        isGettingStudies,
        searchResultCount,
        isShowingResults,
        searchResults,
        amrFilterValues,
        ctFilterValues,
        autoCompleteItems,
        activeTab,
        filterString,
        searchInput,
        toggleIsShowingResults,
        updateFilterString,
        updateSelectStrings,
        handleDropdownFilter,
        handleDateRangeFilter,
        handleSampleSizeFilter,
        handleYearRangeFilter,
        handleBooleanFilter,
        updateStudyData,
        getNextStudyData,
        updateAutoCompleteItems,
        addFilterSet,
        updateFilterSets,
        removeFilterSet,
        setActiveTab,
        resetFilterSets,
        setSearchInput,
        callExport,
        retrievingStudiesCsv
    };
};
